import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday } from 'date-fns';
import { cn } from '../../lib/utils';

export function CalendarPage() {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const days = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth),
  });

  // Pad the start of the calendar
  const startDay = startOfMonth(currentMonth).getDay();
  const paddingDays = Array(startDay).fill(null);

  return (
    <div className="py-12">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Events Calendar</h1>
          <p className="text-xl text-gray-600">
            View upcoming events and track availability
          </p>
        </div>

        {/* Calendar Controls */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
            className="btn btn-outline btn-md"
          >
            <ChevronLeft size={20} />
            Previous
          </button>

          <h2 className="text-2xl font-bold text-gray-900">
            {format(currentMonth, 'MMMM yyyy')}
          </h2>

          <button
            onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            className="btn btn-outline btn-md"
          >
            Next
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Calendar Grid */}
        <div className="card overflow-hidden">
          {/* Day Headers */}
          <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <div
                key={day}
                className="p-4 text-center text-sm font-semibold text-gray-600"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className="grid grid-cols-7">
            {/* Padding days */}
            {paddingDays.map((_, index) => (
              <div key={`pad-${index}`} className="h-24 bg-gray-50 border-b border-r border-gray-200" />
            ))}

            {/* Actual days */}
            {days.map((day) => (
              <div
                key={day.toISOString()}
                className={cn(
                  'h-24 p-2 border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors',
                  !isSameMonth(day, currentMonth) && 'bg-gray-50 text-gray-400'
                )}
              >
                <div
                  className={cn(
                    'w-8 h-8 flex items-center justify-center rounded-full text-sm',
                    isToday(day) && 'bg-red-600 text-white font-semibold'
                  )}
                >
                  {format(day, 'd')}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Legend */}
        <div className="mt-8 flex flex-wrap gap-6 justify-center">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded bg-green-500" />
            <span className="text-sm text-gray-600">Available</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded bg-yellow-500" />
            <span className="text-sm text-gray-600">Limited</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded bg-red-500" />
            <span className="text-sm text-gray-600">Booked</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded bg-purple-500" />
            <span className="text-sm text-gray-600">Event</span>
          </div>
        </div>
      </div>
    </div>
  );
}
